import AgoraRTC, { AudioEncoderConfigurationPreset, IMicrophoneAudioTrack, IRemoteAudioTrack } from 'agora-rtc-sdk-ng';
import AsyncRetry from 'async-retry';
import i18next from 'i18next';
import {
    activateCurrentMic,
    deactivateCurrentMic,
    NetworkQuality,
    setIsTalking,
    updateNetworkQuality,
} from 'slice/voiceCallSlice';

import joinSoundSrc from '../media/joinSound.mp3';
import { Mic } from '../model/Voice';
import { setTopError } from '../slice/errorSlice';
import { AppDispatch } from '../store';
import { isAgoraInvalidRemoteUserError, isAgoraRemoteUserIsNotPublishedError } from './apiError';

if (process.env.REACT_APP_ENV === 'production') {
    // 3: ERROR. Output logs of the ERROR level.
    AgoraRTC.setLogLevel(3);
}

export const JOIN_SOUND = new Audio(joinSoundSrc);

// codec only affects video call. However, you must specify codec when calling `AgoraRTC.createClient`
const CLIENT = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
const AUDIO_QUALITY: AudioEncoderConfigurationPreset = 'speech_standard';

export const AgoraAPI = {
    // この関数を何度も呼ぶと、agora handlerが重複して登録されるので注意
    initAgoraHandler: (dispatch: AppDispatch): void => {
        CLIENT.on('user-published', async (user, mediaType) => {
            if (mediaType === 'audio') {
                // CLIENT.subscribeはfailすることがあるので1秒毎に10回retryする
                // https://github.com/ReactCorp/Cocalero_mvp/issues/1346
                AsyncRetry(
                    async () => {
                        await CLIENT.subscribe(user, 'audio');
                        user.audioTrack?.play();
                    },
                    { retries: 10, minTimeout: 1000, maxTimeout: 1000 }
                ).catch((err) => {
                    console.error('AgoraRTCClient.subscribe fails ' + err);
                    if (isAgoraInvalidRemoteUserError(err) || isAgoraRemoteUserIsNotPublishedError(err)) {
                        // iOS, Androidでmuteを解除した時にこの関数が呼ばれるが、remoteUserがmute buttonを連打すると、
                        // remoteUserがaudioTrackをpublishしていないのにこのhandlerがよばれてsubscribeに失敗することがある。
                        // そのような場合はtopErrorを表示する必要はない
                        return;
                    }
                    dispatch(setTopError(i18next.t('error.slowNetwork')));
                });
            }
        });

        CLIENT.enableAudioVolumeIndicator();
        CLIENT.on('volume-indicator', (result) => {
            result.forEach((volume) => {
                const level = Math.floor(volume.level);
                const uid = volume.uid;

                const parsedId = typeof uid === 'string' ? parseInt(uid) : uid;
                if (!Number.isNaN(parsedId)) {
                    dispatch(setIsTalking({ userId: parsedId, volume: level }));
                }
            });
        });

        // `CLIENT.on('volume-indicator')`は、userがmuteを押すと呼ばれなくなってしまう。
        // それにより、muteボタンを押したタイミングのsetIsTalkingが保持されてしまい、userがmuteしていても（話していなくても）、setIsTalkingがtrueになってしまう。
        // そこで、userがmuteボタンを押すと`CLIENT.on('user-unpublished')`が呼ばれるので、`CLIENT.on('user-unpublished')`でsetIsTalkingをfalseにする。
        CLIENT.on('user-unpublished', (user, mediaType) => {
            if (mediaType === 'video') {
                return;
            }
            const uid = user.uid;
            const parsedId = typeof uid === 'string' ? parseInt(uid) : uid;
            if (!Number.isNaN(parsedId)) {
                dispatch(setIsTalking({ userId: parsedId, volume: 0 }));
            }
        });

        CLIENT.on('user-joined', () => JOIN_SOUND.play().catch((err) => console.log(err)));
        CLIENT.on('network-quality', (stats) => {
            const up = stats.uplinkNetworkQuality;
            const down = stats.downlinkNetworkQuality;

            if (up === 0 || down === 0) {
                dispatch(updateNetworkQuality(NetworkQuality.Unknown));
            } else if (up === 6 || down === 6) {
                dispatch(updateNetworkQuality(NetworkQuality.Offline));
            } else if (up <= 2 && down <= 2) {
                dispatch(updateNetworkQuality(NetworkQuality.Good));
            } else if (up <= 4 && down <= 4) {
                dispatch(updateNetworkQuality(NetworkQuality.Normal));
            } else {
                dispatch(updateNetworkQuality(NetworkQuality.Bad));
            }
        });

        CLIENT.on('exception', (event) => {
            //     * | 2001   | AUDIO_INPUT_LEVEL_TOO_LOW  | Sent audio volume is too low     |
            //     * | 2002   | AUDIO_OUTPUT_LEVEL_TOO_LOW | Received audio volume is too low     |
            //     * | 4001   | AUDIO_INPUT_LEVEL_TOO_LOW_RECOVER  | Sent audio volume recovers     |
            //     * | 4002   | AUDIO_OUTPUT_LEVEL_TOO_LOW_RECOVER | Received audio volume recovers     |
            const code = event.code;
            if ([2001, 2002, 4001, 4002].includes(code)) {
                return;
            }
            console.error(`agora throws exception code: ${event.code}, message: ${event.msg}, uid: ${event.uid}`);
        });
        AgoraRTC.onMicrophoneChanged = async (info) => {
            const devices = await AgoraRTC.getMicrophones();
            const mics = devices.map((device) => ({ label: device.label, deviceId: device.deviceId }));
            const deviceId = info.device.deviceId;
            if (info.state === 'ACTIVE') {
                dispatch(activateCurrentMic({ deviceId: deviceId, mics: mics }));
            } else if (info.state === 'INACTIVE') {
                dispatch(deactivateCurrentMic({ deviceId: deviceId, mics: mics }));
            }
        };
    },

    startBasicCall: async (channel: string, token: string, userId: number, currentMic: Mic): Promise<void> => {
        console.log('start basic call. VERSION: ' + AgoraRTC.VERSION);

        // Must create localAudioTrack before joining. This code makes popup about mice permission.
        // If create localAudioTrack after joining and user can't allow to use mice, user joins room even though user can't speak
        const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            encoderConfig: AUDIO_QUALITY,
            microphoneId: currentMic.deviceId,
        });
        await CLIENT.join(process.env.REACT_APP_AGORA_APP_ID, channel, token, userId);
        await CLIENT.publish([localAudioTrack]);
    },

    leaveCall: async (): Promise<void> => {
        const localAudioTrack = getLocalAudioTrack();
        localAudioTrack?.close();
        await CLIENT.leave();
    },

    // agora-web-sdk-ng don't have mute API. Instead, you set the volume of localAudioTrack to 0.
    // When isMute is true, set volume to 0. On the other hand, isMute is false, setVolume to the argument value.
    setMute: async (isMute: boolean, volume: number): Promise<void> => {
        const localAudioTrack = getLocalAudioTrack();
        if (isMute) {
            localAudioTrack?.setVolume(0);
        } else {
            localAudioTrack?.setVolume(volume);
        }
    },

    setMyInputVolume: async (isMute: boolean, volume: number): Promise<void> => {
        if (!isMute) {
            const localAudioTrack = getLocalAudioTrack();
            localAudioTrack?.setVolume(volume);
        }
    },

    setMemberOutputVolume: async (memberId: number, volume: number): Promise<void> => {
        const remoteAudioTrack = getRemoteAudioTrack(memberId);
        remoteAudioTrack?.setVolume(volume);
    },

    setMic: async (deviceId: string): Promise<void> => {
        const track = getLocalAudioTrack();
        if (track === null) {
            return;
        }
        track.setDevice(deviceId);
    },

    getUserMics: async (): Promise<MediaDeviceInfo[]> => {
        const mics = await AgoraRTC.getMicrophones();
        if (mics.length === 0) {
            throw new Error('mic is not found');
        }
        return mics;
    },
};

const getLocalAudioTrack = (): IMicrophoneAudioTrack | null => {
    const tracks = CLIENT.localTracks;
    if (tracks.length === 1) {
        // TODO should use User-Defined Type Guards
        const track = tracks[0];
        return track as IMicrophoneAudioTrack;
    }
    return null;
};

const getRemoteAudioTrack = (memberId: number): IRemoteAudioTrack | undefined => {
    const user = CLIENT.remoteUsers.find((user) => user.uid === memberId);
    return user?.audioTrack;
};
